import axios from 'axios';
import { Action, createAction, handleActions } from 'redux-actions';
import { FULFILLED, PENDING, REJECTED } from './action-type.util';
import { IBoltPimCatalog, IBoltPimSyncHistory, IPimLoadComparison, IUpdateBoltPimCatalogBody } from './boltPim.model';
import {
    getAxiosData,
    getRejectedRespData,
    initPagingList,
    IPaging,
    parsePagingToParams,
    parseToPaging,
    SERVER_API_URL,
} from '../config/constants';

export const ACTION_TYPES = {
    UPDATE_STATE: 'boltPim/UPDATE_STATE',
    SET_PAGING: 'boltPim/SET_PAGING',
    RESET_PAGING: 'boltPim/RESET_PAGING',
    SET_HISTORY_PAGING: 'boltPim/SET_HISTORY_PAGING',
    GET: 'boltPim/GET',
    GET_CURRENT: 'boltPim/GET_CURRENT',
    GET_BY_ID: 'boltPim/GET_BY_ID',
    SAVE: 'boltPim/SAVE',
    TRIGGER_SYNC: 'boltPim/TRIGGER_SYNC',
    GET_HISTORY: 'boltPim/GET_HISTORY',
    GET_HISTORY_BY_ID: 'boltPim/GET_HISTORY_BY_ID',
    COMPARE_LOADS: 'boltPim/COMPARE_LOADS',
};

export interface IBoltPimState {
    loading: boolean;
    error: any;
    list: IBoltPimCatalog[];
    data: IBoltPimCatalog;
    paging: IPaging;
    history: IBoltPimSyncHistory[];
    historyItem: IBoltPimSyncHistory;
    historyPaging: IPaging;
    comparison: IPimLoadComparison | null;
}

const initState = {
    loading: false,
    error: null,
    list: [],
    data: {} as IBoltPimCatalog,
    paging: { ...initPagingList },
    history: [],
    historyItem: {} as IBoltPimSyncHistory,
    historyPaging: { ...initPagingList },
    comparison: null,
};

export const updateState = createAction(ACTION_TYPES.UPDATE_STATE);
export const setPaging = createAction(ACTION_TYPES.SET_PAGING);
export const resetPaging = createAction(ACTION_TYPES.RESET_PAGING);
export const setHistoryPaging = createAction(ACTION_TYPES.SET_HISTORY_PAGING);

export const get = createAction<any, any>(ACTION_TYPES.GET, (params?: any) =>
    getAxiosData(
        axios.get(`${SERVER_API_URL}/bolt-pim/catalogs`, {
            params: parsePagingToParams({ ...initPagingList, ...params }),
        }),
    ),
);
export const getCurrent = createAction(ACTION_TYPES.GET_CURRENT, () =>
    getAxiosData(
        axios.get(`${SERVER_API_URL}/bolt-pim/catalogs`, {
            params: parsePagingToParams({ ...initPagingList }),
        }),
    ),
);
export const getById = createAction(ACTION_TYPES.GET_BY_ID, (id: string) =>
    getAxiosData(axios.get(`${SERVER_API_URL}/bolt-pim/catalogs/${id}`)),
);
export const save = createAction(ACTION_TYPES.SAVE, (body: IUpdateBoltPimCatalogBody) =>
    getAxiosData(axios.put(`${SERVER_API_URL}/bolt-pim/catalogs/${body.id}`, body)),
);
export const triggerSync = createAction(ACTION_TYPES.TRIGGER_SYNC, (storeId: number) =>
    getAxiosData(axios.post(`${SERVER_API_URL}/bolt-pim/sync`, { storeId })),
);
export const getHistory = createAction<any, any>(ACTION_TYPES.GET_HISTORY, (params?: any) =>
    getAxiosData(
        axios.get(`${SERVER_API_URL}/bolt-pim/sync-history`, {
            params: parsePagingToParams({ ...initPagingList, ...params }),
        }),
    ),
);
export const getHistoryById = createAction(ACTION_TYPES.GET_HISTORY_BY_ID, (id: number) =>
    getAxiosData(axios.get(`${SERVER_API_URL}/bolt-pim/sync-history/${id}`)),
);
export const compareLoads = createAction(ACTION_TYPES.COMPARE_LOADS, (fromId: number, toId: number) =>
    getAxiosData(axios.post(`${SERVER_API_URL}/bolt-pim/sync-history/compare`, { fromId, toId })),
);

const onPending = (state: IBoltPimState): IBoltPimState => ({ ...state, loading: true });
const onRejected = (state: IBoltPimState, action: Action<any>): IBoltPimState => ({
    ...state,
    error: getRejectedRespData(action.payload),
    loading: false,
});
const onSetItem = (state: IBoltPimState, action: Action<any>): IBoltPimState => ({
    ...state,
    loading: false,
    data: { ...action.payload },
});

export default handleActions<IBoltPimState, any>(
    {
        [ACTION_TYPES.UPDATE_STATE]: (state: IBoltPimState, action: Action<any>): IBoltPimState => ({
            ...state,
            ...action.payload,
        }),
        [ACTION_TYPES.SET_PAGING]: (state: IBoltPimState, action: Action<IPaging>): IBoltPimState => ({
            ...state,
            paging: { ...state.paging, ...parseToPaging(action.payload) },
        }),
        [ACTION_TYPES.RESET_PAGING]: (state: IBoltPimState): IBoltPimState => ({
            ...state,
            paging: { ...initPagingList },
            historyPaging: { ...initPagingList },
        }),
        [ACTION_TYPES.SET_HISTORY_PAGING]: (state: IBoltPimState, action: Action<IPaging>): IBoltPimState => ({
            ...state,
            historyPaging: { ...state.historyPaging, ...parseToPaging(action.payload) },
        }),

        [PENDING(ACTION_TYPES.GET)]: onPending,
        [REJECTED(ACTION_TYPES.GET)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET)]: (state: IBoltPimState, action: Action<any>): IBoltPimState => {
            const { page: list = [], ...paging } = action.payload;
            return { ...state, loading: false, list, paging: { ...state.paging, ...paging } };
        },

        [PENDING(ACTION_TYPES.GET_CURRENT)]: onPending,
        [REJECTED(ACTION_TYPES.GET_CURRENT)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET_CURRENT)]: (state: IBoltPimState, action: Action<any>): IBoltPimState => {
            const { page = [] } = action.payload;
            return { ...state, loading: false, data: { ...(page[0] || {}) } };
        },

        [PENDING(ACTION_TYPES.GET_BY_ID)]: onPending,
        [REJECTED(ACTION_TYPES.GET_BY_ID)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET_BY_ID)]: onSetItem,

        [PENDING(ACTION_TYPES.SAVE)]: onPending,
        [REJECTED(ACTION_TYPES.SAVE)]: onRejected,
        [FULFILLED(ACTION_TYPES.SAVE)]: onSetItem,

        [PENDING(ACTION_TYPES.TRIGGER_SYNC)]: onPending,
        [REJECTED(ACTION_TYPES.TRIGGER_SYNC)]: onRejected,
        [FULFILLED(ACTION_TYPES.TRIGGER_SYNC)]: onSetItem,

        [PENDING(ACTION_TYPES.GET_HISTORY)]: onPending,
        [REJECTED(ACTION_TYPES.GET_HISTORY)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET_HISTORY)]: (state: IBoltPimState, action: Action<any>): IBoltPimState => {
            const { page: history = [], ...historyPaging } = action.payload;
            return { ...state, loading: false, history, historyPaging: { ...state.historyPaging, ...historyPaging } };
        },

        [PENDING(ACTION_TYPES.GET_HISTORY_BY_ID)]: onPending,
        [REJECTED(ACTION_TYPES.GET_HISTORY_BY_ID)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET_HISTORY_BY_ID)]: (state: IBoltPimState, action: Action<any>): IBoltPimState => ({
            ...state,
            loading: false,
            historyItem: { ...action.payload },
        }),

        [PENDING(ACTION_TYPES.COMPARE_LOADS)]: onPending,
        [REJECTED(ACTION_TYPES.COMPARE_LOADS)]: onRejected,
        [FULFILLED(ACTION_TYPES.COMPARE_LOADS)]: (state: IBoltPimState, action: Action<any>): IBoltPimState => ({
            ...state,
            loading: false,
            comparison: { ...action.payload },
        }),
    },
    initState,
);
